import React, { useState } from 'react';
import {
    Search,
    BookOpen,
    Code,
    GraduationCap,
    HelpCircle,
    Video,
    ArrowRight,
    Headphones,
    MessageSquareText
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';
import PopularIntegrations from '../../components/sections/ressources/PopularIntegration';

export default function ResourcesPage() {
    const [searchQuery, setSearchQuery] = useState('');

    const categories = [
        {
            icon: <BookOpen className="w-5 h-5 text-blue-600" />,
            bgColor: 'bg-blue-50',
            title: 'Guides & Tutoriels',
            description: 'Des guides étape par étape pour vous aider à démarrer et à optimiser votre utilisation.',
            links: ['Démarrer avec IZI Call Center', 'Gérer vos appels', 'Configurer votre équipe'],
            href: '/resources/guides'
        },
        {
            icon: <Code className="w-5 h-5 text-emerald-600" />,
            bgColor: 'bg-emerald-50',
            title: 'Documentation API',
            description: 'Intégrez IZI Call Center à vos applications avec notre API puissante et flexible.',
            links: ['Référence API', 'Authentification', 'Webhooks'],
            href: '/resources/api'
        },
        {
            icon: <GraduationCap className="w-5 h-5 text-purple-600" />,
            bgColor: 'bg-purple-50',
            title: 'Académie',
            description: 'Formations, webinaires et certifications pour monter en compétence.',
            links: ['Webinaires en direct', 'Formations en ligne', 'Certifications'],
            href: '/resources/academy'
        },
        {
            icon: <HelpCircle className="w-5 h-5 text-amber-600" />,
            bgColor: 'bg-amber-50',
            title: 'FAQ',
            description: 'Trouvez rapidement des réponses aux questions les plus fréquentes.',
            links: ['Comptes & facturation', 'Fonctionnalités', 'Sécurité & confidentialité'],
            href: '/resources/faq'
        },
        {
            icon: <Video className="w-5 h-5 text-rose-600" />,
            bgColor: 'bg-rose-50',
            title: 'Vidéos',
            description: 'Découvrez nos tutoriels vidéo pour maîtriser toutes les fonctionnalités.',
            links: ['Prise en main', 'Fonctionnalités avancées', 'Astuces & bonnes pratiques'],
            href: '/resources/videos'
        }
    ];

    const integrations = [
        { name: 'WhatsApp', icon: '/integrations/whatsapp.png' },
        { name: 'Microsoft 365', icon: '/integrations/microsoft365.png' },
        { name: 'Google Workspace', icon: '/integrations/google-workspace.png' },
        { name: 'Slack', icon: '/integrations/slack.png' },
        { name: 'HubSpot', icon: '/integrations/hubspot.png' },
        { name: 'Salesforce', icon: '/integrations/salesforce.png' },
        { name: 'Zapier', icon: '/integrations/zapier.png' }
    ];

    return (
        <PublicPageLayout>
             <div className="max-w-7xl mx-auto py-10 px-6 lg:px-12 space-y-12">

                    {/* ================= HERO SECTION ================= */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        
                        {/* Côté Gauche : Titre + Recherche */}
                        <div className="lg:col-span-6 space-y-6">
                            <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">
                                RESSOURCES
                            </span>
                            
                            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                                Tout ce qu'il vous faut<br />
                                pour réussir avec <span className="text-blue-600">IZI.</span>
                            </h1>

                            <p className="text-slate-500 text-xs sm:text-sm leading-relaxed max-w-lg">
                                Guides, documentation, API, blog et support : accédez à toutes les ressources pour tirer le meilleur de votre plateforme.
                            </p>

                            {/* Barre de recherche */}
                            <div className="flex items-center bg-white p-1.5 rounded-2xl border border-slate-200/80 shadow-sm max-w-md focus-within:border-blue-500 transition-all">
                                <div className="pl-3.5 text-slate-400">
                                    <Search size={18} />
                                </div>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher une ressource..."
                                    className="w-full px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 bg-transparent focus:outline-none"
                                />
                                <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all shrink-0">
                                    Rechercher
                                </button>
                            </div>
                        </div>

                        {/* Côté Droit : Mockup Ordinateur */}
                        <div className="lg:col-span-6 flex justify-center items-center relative">
                            <img
                                src="/ressources/hero.png"
                                alt="Centre de ressources IZI Call Center"
                                className="w-full max-w-lg object-contain"
                            />
                        </div>

                    </div>

                    {/* ================= GRILLE DES CATÉGORIES (5 CARTES) ================= */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-5 border border-slate-200/70 shadow-sm flex flex-col justify-between hover:shadow-md transition-all group"
                            >
                                <div className="space-y-3">
                                    {/* Icône de la catégorie */}
                                    <div className={`w-10 h-10 rounded-xl ${cat.bgColor} flex items-center justify-center shrink-0`}>
                                        {cat.icon}
                                    </div>

                                    {/* Titre & Description */}
                                    <h3 className="text-xs font-extrabold text-slate-900">{cat.title}</h3>
                                    <p className="text-[11px] text-slate-500 leading-relaxed">
                                        {cat.description}
                                    </p>

                                    {/* Liste à puces */}
                                    <ul className="space-y-1.5 pt-2 border-t border-slate-100">
                                        {cat.links.map((link, lIndex) => (
                                            <li key={lIndex} className="text-[11px] text-slate-600 flex items-center gap-1.5">
                                                <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0"></span>
                                                <span className="truncate">{link}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Lien d'action */}
                                <a
                                    href={cat.href}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-600 group-hover:text-blue-700 pt-4 mt-2"
                                >
                                    <span>Voir plus</span>
                                    <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                                </a>
                            </div>
                        ))}
                    </div>

                    {/* ================= SECTION INTÉGRATIONS POPULAIRES ================= */}
                     <PopularIntegrations />

                    {/* ================= BANNIÈRE D'ASSISTANCE (SOMBRE) ================= */}
                    <div className="bg-[#0B1B3D] rounded-3xl p-6 lg:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl relative overflow-hidden">
                        
                        {/* Côté Gauche : Casque / Icônes + Texte */}
                        <div className="flex items-center gap-5 z-10">
                            <div className="w-14 h-14 rounded-2xl bg-blue-600/20 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/20">
                                <Headphones size={28} />
                            </div>

                            <div className="space-y-1">
                                <h3 className="text-base lg:text-lg font-extrabold text-white">
                                    Besoin d'aide pour trouver la bonne ressource ?
                                </h3>
                                <p className="text-slate-400 text-xs">
                                    Notre équipe support est disponible 24/7 pour vous accompagner.
                                </p>
                            </div>
                        </div>

                        {/* Côté Droit : Boutons d'action */}
                        <div className="flex items-center gap-3 w-full md:w-auto z-10 shrink-0">
                            <a
                                href="/contact"
                                className="w-full md:w-auto px-4 py-2.5 rounded-xl border border-slate-600 text-white font-bold text-xs flex items-center justify-center gap-2 hover:bg-white/5 transition-all"
                            >
                                <Headphones size={14} />
                                <span>Contacter le support</span>
                            </a>

                            <button
                                className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-amber-500/20"
                            >
                                <MessageSquareText size={14} />
                                <span>Discuter maintenant</span>
                            </button>
                        </div>

                    </div>

                </div>
        </PublicPageLayout>
    );
}